import { memo, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { WireMessage, WireRun } from '@converoom/contracts';
import type { Me } from '../lib/types';
import { time, usd, PROVIDER_NAME, RUN_STATE_TEXT, BLOCK_TEXT } from '../lib/format';

/** Markdown is rendered WITHOUT raw HTML (react-markdown default) and links open safely. */
function Body({ text }: { text: string }) {
  return (
    <div className="msg-body">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer nofollow">
              {children}
            </a>
          ),
          img: ({ alt }) => <span>[görsel: {alt}]</span>, // no remote image loading (tracking/SSRF-by-client)
        }}
      >
        {text}
      </Markdown>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="btn small ghost"
      onClick={async () => {
        await navigator.clipboard?.writeText(text).catch(() => {});
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? 'Kopyalandı' : 'Kopyala'}
    </button>
  );
}

interface Props {
  m: WireMessage;
  me: Me;
  run: (WireRun & { partial?: string }) | undefined;
  replyTarget: WireMessage | undefined;
  onQuote: () => void;
  onEdit: () => void;
}

export const MessageItem = memo(function MessageItem({ m, me, run, replyTarget, onQuote, onEdit }: Props) {
  const provider = m.provider ?? run?.provider ?? 'mock';
  if (m.authorKind === 'system') {
    return (
      <article className="msg system" aria-label="Sistem bildirimi">
        <div className="msg-head">
          <span className="msg-author">Converoom</span>
          <time className="msg-time">{time(m.createdAt)}</time>
        </div>
        <div className="msg-body">
          {m.text}
          {run?.blockReason && BLOCK_TEXT[run.blockReason] && (
            <p className="hint">{BLOCK_TEXT[run.blockReason]}</p>
          )}
        </div>
      </article>
    );
  }
  const isAi = m.authorKind === 'ai';
  const author = isAi ? (PROVIDER_NAME[provider] ?? provider) : m.authorName;
  return (
    <article className={`msg ${isAi ? `ai voice-${provider}` : 'user'}`} aria-label={`${author} mesajı`}>
      <div className="msg-head">
        <span className="msg-author">{author}</span>
        {isAi && <span className="msg-model">{m.model}</span>}
        <time className="msg-time" dateTime={m.createdAt}>
          {time(m.createdAt)}
        </time>
        {m.editedAt && <span className="msg-time">(düzenlendi)</span>}
      </div>
      <div className="msg-actions">
        <CopyButton text={m.text} />
        <button className="btn small ghost" onClick={onQuote}>
          Alıntıla
        </button>
        {m.authorKind === 'user' && m.authorUserId === me.id && (
          <button className="btn small ghost" onClick={onEdit}>
            Düzenle
          </button>
        )}
      </div>
      {replyTarget && (
        <div className="quote">
          {replyTarget.authorName}: {replyTarget.text.slice(0, 160)}
        </div>
      )}
      <Body text={m.text} />
      {isAi && run && <RunLine run={run} me={me} />}
    </article>
  );
});

/** Explicit, honest run facts: who paid, state, and an ESTIMATED cost (only shown to the payer). */
export function RunLine({ run, me }: { run: WireRun; me: Me }) {
  const bad = ['FAILED', 'BLOCKED'].includes(run.state);
  const warn = ['UNKNOWN_EXTERNAL_RESULT', 'CANCELLED'].includes(run.state);
  return (
    <div className="run-line">
      <span>Ödeyen: {run.payerUserId === me.id ? 'siz' : 'başlatan üye'}</span>
      <span className={bad ? 'state-bad' : warn ? 'state-warn' : undefined}>
        {RUN_STATE_TEXT[run.state] ?? run.state}
      </span>
      {run.payerUserId === me.id && run.estimatedCostMicros !== null && (
        <span>tahmini {usd(run.estimatedCostMicros)}</span>
      )}
      {run.provider === 'mock' && <span className="state-warn">Test sağlayıcısı — gerçek AI değil</span>}
    </div>
  );
}

export function PendingRun({
  run,
  mine,
  onCancel,
}: {
  run: WireRun & { partial: string };
  mine: boolean;
  onCancel: () => void;
}) {
  return (
    <article
      className={`msg ai voice-${run.provider}`}
      aria-busy="true"
      aria-label={`${PROVIDER_NAME[run.provider] ?? run.provider} yanıtı`}
    >
      <div className="msg-head">
        <span className="msg-author">{PROVIDER_NAME[run.provider] ?? run.provider}</span>
        <span className="msg-model">{run.model}</span>
      </div>
      <div className="msg-actions" style={{ opacity: 1 }}>
        {mine && run.state !== 'CANCEL_REQUESTED' && (
          <button className="btn small danger" onClick={onCancel}>
            Durdur
          </button>
        )}
      </div>
      <div className="msg-body">
        {run.partial ? (
          <Body text={run.partial} />
        ) : (
          <span className="hint">{RUN_STATE_TEXT[run.state]}</span>
        )}{' '}
        <span className="caret" aria-hidden="true" />
      </div>
      <div className="run-line">
        <span>Ödeyen: {mine ? 'siz' : 'başlatan üye'}</span>
        <span>{RUN_STATE_TEXT[run.state]}</span>
        {mine && <span className="hint">Durdurmak, işlenmiş kısmın ücretsiz olduğu anlamına gelmez.</span>}
      </div>
    </article>
  );
}
